/**
 * The hours rules the Slack form applies, applied here too.
 *
 * Copied deliberately rather than shared: they live in the API's validateDailyReport, which the browser
 * cannot import, and the alternative is a round trip per keystroke. They are small and they are the
 * same rules — a difference between the two surfaces would be a bug either way round.
 */
export const TARGET_HOURS = 8;
const GRANULARITY = 0.25;
const MAX_TOTAL = 24;

export type HoursErrors = Record<string, string>;

/**
 * The day's hours with the sole product's answer filled in, mirroring the autofill the Slack form does
 * (apps/api dailyReportHours `autofillHours`). One product carrying the whole day is a usual day often
 * enough to be worth answering for people; against several it would be a wrong answer on every line,
 * which is why ticking a second product takes it back out again.
 *
 * Derived on every read rather than written into the form's state, so there is nothing to take back out.
 * A field that has been typed in is left alone, an emptied one included — Slack refills those, but it
 * only redraws between submits, whereas here the field would refill itself as the last character was
 * deleted.
 */
export const withAutofilledHours = (
  products: string[],
  hours: Record<string, string>,
  /** What the day still needs once its time off is counted: half a day off leaves 4. */
  target: number = TARGET_HOURS,
): Record<string, string> => {
  const [soleProduct] = products;
  if (products.length !== 1 || hours[soleProduct] !== undefined || target <= 0) return hours;

  return { ...hours, [soleProduct]: String(target) };
};

/** The hours a day still needs after its time off and what is already on it, rounded down to quarter hours. */
export const targetAfterTimeOff = (timeOffMinutes: number): number =>
  Math.max(0, Math.floor((TARGET_HOURS - timeOffMinutes / 60) / GRANULARITY) * GRANULARITY);

const cents = (hours: number): number => Math.round(hours * 100);

export interface HoursCheck {
  errors: HoursErrors;
  /** Worth asking about once, not refusing: a very long or unusually short day. */
  note?: 'long' | 'short';
  total: number;
}

export const checkHours = (
  products: string[],
  hours: Record<string, string>,
  t: (key: string, vars?: Record<string, unknown>) => string,
  target: number = TARGET_HOURS,
): HoursCheck => {
  const errors: HoursErrors = {};
  let total = 0;

  products.forEach(product => {
    const raw = (hours[product] ?? '').trim();
    const parsed = Number(raw.replace(',', '.'));

    if (raw === '') {
      errors[product] = t('phillReport.hoursMissing');
      return;
    }

    if (!Number.isFinite(parsed) || parsed <= 0) {
      errors[product] = t('phillReport.hoursInvalid');
      return;
    }
    // Quarter hours, as the hint says: 15 minutes is 0.25. A stray 3.7 is nearly always a typo for 3.75.
    if (cents(parsed) % cents(GRANULARITY) !== 0) {
      errors[product] = t('phillReport.hoursGranularity');
      return;
    }

    total += parsed;
  });

  if (Object.keys(errors).length === 0 && total > MAX_TOTAL) {
    products.forEach(product => {
      errors[product] = t('phillReport.hoursTotal', { max: MAX_TOTAL });
    });
  }

  if (Object.keys(errors).length > 0) return { errors, total };

  // Asked once, then taken at their word — some days really are eleven hours.
  if (total >= TARGET_HOURS * 2) return { errors, note: 'long', total };
  if (total < target) return { errors, note: 'short', total };

  return { errors, total };
};
